import axios from "axios";
import type { ExpertProfile } from "../../types/Profiles/expertProfile.types";

const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:9000";

const axiosInstance = axios.create({
  baseURL: `${backendUrl}/api`,
  headers: { "Content-Type": "application/json" },
});

const normalizeList = (rows: any[], key: string) =>
  Array.isArray(rows)
    ? rows.map((row) => String(row?.[key] || "").trim()).filter(Boolean)
    : [];

const getUserDetails = (userData: any) => userData?.userDetails || userData || {};

const normalizeExpertProfile = (data: any, userData?: any): ExpertProfile => {
  const user = getUserDetails(userData);
  const fullName = [user?.nom_user, user?.prenom_user]
    .map((value) => String(value || "").trim())
    .filter(Boolean)
    .join(" ");

  return {
    identity: {
      companyName: data?.company_name || user?.company || user?.username_user || user?.username || "",
      logo: data?.logo || user?.img || "",
      expertType: data?.company_profile_type || data?.company_type || "bureau_etudes",
      siteName: data?.site_name || "",
      wilaya: data?.wilaya || user?.wilaya || "",
      address: data?.factory_address || "",
      location: data?.factory_location || "",
      contactName: data?.contact_name || fullName || user?.username_user || user?.username || "",
      contactEmail: data?.email || user?.email_user || user?.email || "",
      contactPhone: data?.phone || user?.phone_user || user?.phone || "",
      status: data?.status || "active",
      aidaBadge: data?.aida_badge || "aida_verified",
      adhesionId: data?.id_adhesion || null,
    },
    domains: normalizeList(data?.products, "product_name"),
    certifications: normalizeList(data?.certifications, "certification_name"),
    supportNeeds: normalizeList(data?.support_needs, "support_need_text"),
    activity: [
      data?.created_at ? { label: "Creation adhesion", value: data.created_at } : null,
      data?.response_date ? { label: "Date de reponse", value: data.response_date } : null,
      data?.respondent_name ? { label: "Repondant", value: data.respondent_name } : null,
    ].filter(Boolean) as Array<{ label: string; value: string }>,
  };
};

export const fetchExpertProfile = async (idUser: number | string): Promise<ExpertProfile | null> => {
  try {
    const [adhesionResponse, userResponse] = await Promise.all([
      axiosInstance.get(`/adherer/user/${idUser}`, { params: { t: Date.now() } }).catch(() => ({ data: null })),
      axiosInstance.get(`/userform/${idUser}`).catch(() => ({ data: null })),
    ]);

    return normalizeExpertProfile(adhesionResponse.data?.data || null, userResponse.data);
  } catch (error) {
    console.error("Erreur fetchExpertProfile:", error);
    return null;
  }
};

export const updateExpertProfile = async (payload: {
  companyName: string;
  expertType: string;
  siteName: string;
  wilaya: string;
  address: string;
  location: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  status: string;
  aidaBadge: string;
  domains: string[];
  certifications: string[];
  supportNeeds: string[];
}, userId?: number | string | null) => {
  const response = await axiosInstance.post("/adherer/update-profile", {
    user_id: userId,
    company_name: payload.companyName,
    company_profile_type: payload.expertType,
    site_name: payload.siteName,
    wilaya: payload.wilaya,
    factory_address: payload.address,
    factory_location: payload.location,
    contact_name: payload.contactName,
    email: payload.contactEmail,
    phone: payload.contactPhone,
    status: payload.status,
    aida_badge: payload.aidaBadge,
    domains: payload.domains,
    certifications: payload.certifications,
    support_needs: payload.supportNeeds,
  });
  return response.data;
};

export const uploadExpertLogo = async (file: File, userId?: number | string | null) => {
  const formData = new FormData();
  formData.append("logo", file);
  formData.append("user_id", String(userId || ""));

  const response = await axiosInstance.post("/adherer/update-profile", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return response.data;
};
